import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContexte';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Icon from '../ui/Icon';
import './CreateSaisonPage.css';

const EMPTY = {
    title: '',
    startDate: '',
    endDate: '',
    publicationChannel: '',
    attackGrade: '',
    defenseGrade: '',
    channelConfigId: '',
    description: '',
    autoPublish: false,
    includeTempo: true
};

const REQUIRED = ['title', 'startDate', 'endDate', 'publicationChannel', 'attackGrade', 'defenseGrade', 'description'];

/**
 * Fenêtre de création d'une saison (ouverte depuis la page Saisons).
 * `onClose(created)` est appelé à la fermeture ; created = true si une saison a été créée.
 */
const CreateSaisonPage = ({ onClose }) => {
    const { serverId } = useParams();
    const { user } = useAuth();
    const { success, error: showError, warning } = useNotification();
    const { t } = useLanguage();

    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [salons, setSalons] = useState([]);
    const [attackBaremes, setAttackBaremes] = useState([]);
    const [defenseBaremes, setDefenseBaremes] = useState([]);
    const [channelConfigs, setChannelConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const setField = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: undefined }));
    };

    useEffect(() => {
        if (!serverId) return;
        const load = async () => {
            setLoading(true);
            try {
                const channelsRes = await api.get(`/discord/channels/${serverId}`)
                    .catch(() => api.get(`/servers/${serverId}`).then(r => ({ data: r.data?.salons || [] })));
                setSalons((channelsRes.data || []).filter(c => c.type === 0).sort((a, b) => a.name.localeCompare(b.name)));

                const baremesRes = await api.get(`/baremes/${serverId}`);
                const baremes = baremesRes.data || [];
                setAttackBaremes(baremes.filter(b => b.type === 'attaque'));
                setDefenseBaremes(baremes.filter(b => b.type === 'défense'));

                const configs = await api.get(`/channel-configs/${serverId}`).then(r => r.data || []).catch(() => []);
                setChannelConfigs(configs);
                const defaultConfig = configs.find(c => c.isDefault);
                if (defaultConfig) setForm(prev => ({ ...prev, channelConfigId: defaultConfig._id }));
            } catch (err) {
                if (err.response?.status === 503) warning(t('createSeasonModal.botOffline', 'Le bot Discord n\'est pas connecté, réessaie dans quelques instants.'));
                else showError(t('createSeasonModal.loadError', 'Impossible de charger les salons et les barèmes du serveur.'));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [serverId, showError, warning, t]);

    // Échap pour fermer
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose?.(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, saving]);

    const validate = () => {
        const next = {};
        REQUIRED.forEach(field => {
            if (!String(form[field] || '').trim()) next[field] = t('createSeasonModal.required', 'Champ obligatoire');
        });
        if (form.startDate && form.endDate && new Date(form.startDate) >= new Date(form.endDate)) {
            next.endDate = t('createSeasonModal.endAfterStart', 'La date de fin doit être après la date de début');
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSaving(true);
        try {
            await api.post('/create-saison', {
                ...form,
                serverId,
                title: form.title.trim(),
                description: form.description.trim(),
                channelConfigId: form.channelConfigId || null,
                modifiedBy: user?.username || 'Inconnu'
            });
            success(t('createSeasonModal.created', 'Saison « {title} » créée', { title: form.title.trim() }));
            onClose?.(true);
        } catch (err) {
            showError(err?.response?.data?.error || t('createSeasonModal.createError', 'Erreur lors de la création de la saison'));
        } finally {
            setSaving(false);
        }
    };

    const filled = REQUIRED.filter(field => String(form[field] || '').trim()).length;
    const progress = Math.round((filled / REQUIRED.length) * 100);

    const Field = ({ name, label, children, hint }) => (
        <label className={`c2-field${errors[name] ? ' is-invalid' : ''}`}>
            <span className="c2-field__label">{label}</span>
            {children}
            {errors[name] ? <span className="create-season__error">{errors[name]}</span> : hint && <span className="c2-field__hint">{hint}</span>}
        </label>
    );

    return (
        <div className="c2-scope c2-modal-backdrop" role="presentation" onClick={() => !saving && onClose?.(false)}>
            <div
                className="c2-card c2-modal create-season"
                role="dialog"
                aria-modal="true"
                aria-labelledby="create-season-title"
                onClick={e => e.stopPropagation()}
            >
                <div className="c2-modal__head">
                    <h2 id="create-season-title">{t('createSeasonModal.title', 'Nouvelle saison')}</h2>
                    <button type="button" className="c2-icon-btn" onClick={() => onClose?.(false)} disabled={saving} aria-label={t('common.close', 'Fermer')}>
                        <Icon name="close" size={20} />
                    </button>
                </div>

                <div className="create-season__progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label={t('createSeasonModal.progress', 'Champs obligatoires remplis')}>
                    <div className="create-season__progress-bar" style={{ width: `${progress}%` }} />
                </div>

                {loading ? (
                    <div className="c2-loading">
                        <div className="c2-spinner" />
                        <p>{t('createSeasonModal.loading', 'Chargement des salons et des barèmes...')}</p>
                    </div>
                ) : (
                    <form className="create-season__form" onSubmit={submit} noValidate>
                        <fieldset className="create-season__group">
                            <legend><Icon name="trophy" size={16} />{t('createSeasonModal.season', 'Saison')}</legend>
                            <div className="create-season__grid">
                                {Field({
                                    name: 'title',
                                    label: t('createSeasonModal.name', 'Nom de la saison'),
                                    children: <input className="c2-input" value={form.title} onChange={e => setField('title', e.target.value)} placeholder={t('createSeasonModal.namePlaceholder', 'Ex : Saison 12 — Automne')} autoFocus />
                                })}
                                {Field({
                                    name: 'publicationChannel',
                                    label: t('createSeasonModal.publicationChannel', 'Salon de publication'),
                                    children: (
                                        <select className="c2-select" value={form.publicationChannel} onChange={e => setField('publicationChannel', e.target.value)}>
                                            <option value="">{t('createSeasonModal.pickChannel', '— Choisir un salon —')}</option>
                                            {salons.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
                                        </select>
                                    )
                                })}
                                {Field({
                                    name: 'startDate',
                                    label: t('createSeasonModal.startDate', 'Date de début'),
                                    children: <input type="date" className="c2-input" value={form.startDate} onChange={e => setField('startDate', e.target.value)} />
                                })}
                                {Field({
                                    name: 'endDate',
                                    label: t('createSeasonModal.endDate', 'Date de fin'),
                                    children: <input type="date" className="c2-input" value={form.endDate} min={form.startDate || undefined} onChange={e => setField('endDate', e.target.value)} />
                                })}
                            </div>
                        </fieldset>

                        <fieldset className="create-season__group">
                            <legend><Icon name="scale" size={16} />{t('createSeasonModal.baremesChannels', 'Barèmes et salons')}</legend>
                            <div className="create-season__grid">
                                {Field({
                                    name: 'attackGrade',
                                    label: t('createSeasonModal.attackBareme', 'Barème attaque'),
                                    hint: attackBaremes.length === 0 ? t('createSeasonModal.noAttackBareme', 'Aucun barème d\'attaque : crée-en un dans Barèmes.') : null,
                                    children: (
                                        <select className="c2-select" value={form.attackGrade} onChange={e => setField('attackGrade', e.target.value)}>
                                            <option value="">{t('createSeasonModal.pickBareme', '— Choisir un barème —')}</option>
                                            {attackBaremes.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                                        </select>
                                    )
                                })}
                                {Field({
                                    name: 'defenseGrade',
                                    label: t('createSeasonModal.defenseBareme', 'Barème défense'),
                                    hint: defenseBaremes.length === 0 ? t('createSeasonModal.noDefenseBareme', 'Aucun barème de défense : crée-en un dans Barèmes.') : null,
                                    children: (
                                        <select className="c2-select" value={form.defenseGrade} onChange={e => setField('defenseGrade', e.target.value)}>
                                            <option value="">{t('createSeasonModal.pickBareme', '— Choisir un barème —')}</option>
                                            {defenseBaremes.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                                        </select>
                                    )
                                })}
                                {channelConfigs.length > 0 && Field({
                                    name: 'channelConfigId',
                                    label: t('createSeasonModal.channelConfig', 'Configuration de salons'),
                                    hint: t('createSeasonModal.channelConfigHint', 'Salons d\'attaque et de défense pris en compte pour cette saison'),
                                    children: (
                                        <select className="c2-select" value={form.channelConfigId} onChange={e => setField('channelConfigId', e.target.value)}>
                                            <option value="">{t('createSeasonModal.defaultConfig', 'Configuration par défaut du serveur')}</option>
                                            {channelConfigs.map(c => <option key={c._id} value={c._id}>{c.name}{c.isDefault ? ` ${t('createSeasonModal.defaultSuffix', '(défaut)')}` : ''}</option>)}
                                        </select>
                                    )
                                })}
                            </div>
                        </fieldset>

                        <fieldset className="create-season__group">
                            <legend><Icon name="pencil" size={16} />{t('createSeasonModal.descOptions', 'Description et options')}</legend>
                            {Field({
                                name: 'description',
                                label: t('createSeasonModal.description', 'Description'),
                                children: (
                                    <textarea
                                        className="c2-input create-season__textarea"
                                        rows={3}
                                        value={form.description}
                                        onChange={e => setField('description', e.target.value)}
                                        placeholder={t('createSeasonModal.descPlaceholder', 'Objectifs, récompenses, règles particulières...')}
                                    />
                                )
                            })}
                            <div className="create-season__checks">
                                <label className="create-season__check">
                                    <input type="checkbox" className="c2-check" checked={form.autoPublish} onChange={e => setField('autoPublish', e.target.checked)} />
                                    {t('createSeasonModal.autoPublish', 'Publier automatiquement le classement en fin de saison')}
                                </label>
                                <label className="create-season__check">
                                    <input type="checkbox" className="c2-check" checked={form.includeTempo} onChange={e => setField('includeTempo', e.target.checked)} />
                                    {t('createSeasonModal.includeTempo', 'Inclure les points tempo')}
                                </label>
                            </div>
                        </fieldset>

                        <div className="c2-modal__actions">
                            <button type="button" className="c2-btn c2-btn--ghost" onClick={() => onClose?.(false)} disabled={saving}>{t('common.cancel', 'Annuler')}</button>
                            <button type="submit" className="c2-btn c2-btn--primary" disabled={saving}>
                                <Icon name="plus" size={18} />
                                {saving ? t('createSeasonModal.creating', 'Création...') : t('createSeasonModal.create', 'Créer la saison')}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default CreateSaisonPage;
